import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Users, Search, ChevronRight, CreditCard, KeyRound, Wallet,
  X, RefreshCw, Loader2, AlertTriangle, ArrowLeft, Copy, Check,
  ShieldOff, Trash2, Send, Eye, Calendar, Activity, Plus, MoreHorizontal,
  Zap,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Skeleton, EmptyState, Spinner, Dialog } from '../components/Dialog';
import { Sparkline } from '../components/Sparkline';
import { OperatorStat } from '../components/OperatorStat';
import { adminFetch } from '../adminApi';
import { cn, formatCompact, formatNumber, formatRelative, formatUSD } from '../lib/utils';

/**
 * CustomersPage — operator-side customer list + detail.
 *
 * Route /customers         → list view with search + filter
 * Route /customers/:id     → list view + detail panel opens for that id
 *
 * All management actions hit /admin/* — these are operator-only endpoints.
 */
export default function CustomersPage() {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(customerId || null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Sync URL → selected customer
  useEffect(() => {
    setSelectedId(customerId || null);
  }, [customerId]);

  function openCustomer(id) {
    navigate(`/customers/${id}`);
  }
  function closeCustomer() {
    navigate('/customers');
  }

  // Fetch list
  async function loadList() {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetch('/admin/customers?limit=200');
      setCustomers(data.customers || []);
    } catch (e) {
      setError(e.message || 'failed_to_load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadList(); }, []);

  // Fetch detail when selection changes
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    adminFetch(`/admin/customers/${selectedId}`)
      .then((d) => { if (!cancelled) { setDetail(d); setDetailLoading(false); } })
      .catch((e) => { if (!cancelled) { setDetail({ error: e.message }); setDetailLoading(false); } });
    return () => { cancelled = true; };
  }, [selectedId]);

  // Filtered list
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (planFilter !== 'all' && c.plan_code !== planFilter) return false;
      if (needle && !c.email.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [customers, search, planFilter]);

  const plans = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of customers) {
      if (!seen.has(c.plan_code)) {
        seen.add(c.plan_code);
        out.push(c.plan_code);
      }
    }
    return out.sort();
  }, [customers]);

  const totalCustomers = customers.length;
  const paid = customers.filter((c) => c.plan_code && c.plan_code !== 'free').length;
  const active30d = customers.filter((c) => (c.month_calls || 0) > 0).length;
  const heavy = customers.filter((c) => (c.month_calls || 0) > 1000).length;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Customers"
        description="Browse, search, and manage every account on Datames."
        actions={
          <Button variant="secondary" size="sm" onClick={loadList}>
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <OperatorStat
          icon={Users}
          label="Total"
          value={loading ? null : formatNumber(totalCustomers)}
          hint={loading ? 'Loading…' : 'All accounts ever created'}
          tone="neutral"
          loading={loading}
        />
        <OperatorStat
          icon={CreditCard}
          label="Paid"
          value={loading ? null : formatNumber(paid)}
          hint={loading ? 'Loading…' : `${totalCustomers - paid} on Free`}
          tone="brand"
          loading={loading}
        />
        <OperatorStat
          icon={Activity}
          label="Active (30d)"
          value={loading ? null : formatNumber(active30d)}
          hint={loading ? 'Loading…' : 'Made at least one call'}
          tone="success"
          loading={loading}
        />
        <OperatorStat
          icon={Zap}
          label="Heavy users"
          value={loading ? null : formatNumber(heavy)}
          hint={loading ? 'Loading…' : '>1000 calls in 30d'}
          tone="warning"
          loading={loading}
        />
      </div>

      {/* Search + filters */}
      <Card>
        <Card.Body className="p-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email…"
                className="pl-9"
              />
            </div>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
            >
              <option value="all">All plans</option>
              {plans.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <Badge variant="default">{filtered.length} / {totalCustomers}</Badge>
          </div>
        </Card.Body>
      </Card>

      {/* List + detail */}
      <div className={`grid grid-cols-1 ${selectedId ? 'lg:grid-cols-[1fr_2fr]' : ''} gap-4`}>
        {/* List */}
        <Card>
          <Card.Header>
            <Card.Title>{planFilter === 'all' ? 'All customers' : `Plan: ${planFilter}`}</Card.Title>
            <Card.Subtitle>Click a row to inspect.</Card.Subtitle>
          </Card.Header>
          <Card.Body className="p-0">
            {error ? (
              <div className="p-6 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> {error}
              </div>
            ) : loading ? (
              <div className="p-8 flex items-center justify-center"><Spinner size={20} /></div>
            ) : filtered.length === 0 ? (
              <div className="p-8">
                <EmptyState icon={Users} title="No customers" description={search ? 'Try a different search.' : 'Customers will appear here.'} />
              </div>
            ) : (
              <ul className="divide-y divide-border max-h-[calc(100vh-22rem)] overflow-y-auto">
                {filtered.map((c) => (
                  <li
                    key={c.id}
                    onClick={() => openCustomer(c.id)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors',
                      selectedId === c.id ? 'bg-brand-softest' : 'hover:bg-surface-2/40'
                    )}
                  >
                    <Avatar email={c.email} plan={c.plan_code} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-mono text-fg truncate">{c.email}</div>
                      <div className="text-2xs text-fg-muted flex items-center gap-1.5 mt-0.5">
                        <Badge variant={c.plan_code === 'free' ? 'default' : 'brand'} className="text-2xs">{c.plan_code}</Badge>
                        <span>·</span>
                    <span>{formatNumber(c.month_calls || 0)} calls / 30d</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-fg-subtle shrink-0" />
                  </li>
                ))}
              </ul>
            )}
          </Card.Body>
        </Card>

        {/* Detail panel */}
        {selectedId && (
          <CustomerDetail
            customerId={selectedId}
            detail={detail}
            loading={detailLoading}
            onClose={closeCustomer}
            onChanged={() => {
              // refresh both list (plan/credits may have changed) and detail
              loadList();
              setSelectedId(null);
              setTimeout(() => setSelectedId(selectedId), 50);
            }}
          />
        )}
      </div>
    </div>
  );
}

function Avatar({ email, plan }) {
  const initial = (email || '?')[0].toUpperCase();
  return (
    <div className={cn(
      'h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold uppercase shrink-0',
      plan === 'free'
        ? 'bg-surface-2 text-fg-muted border border-border'
        : 'bg-gradient-to-br from-brand to-orange-400 text-white'
    )}>
      {initial}
    </div>
  );
}

/* =============================================================================
   CUSTOMER DETAIL — full info, all keys, plan + actions
   ============================================================================= */
function CustomerDetail({ customerId, detail, loading, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [creditsDialog, setCreditsDialog] = useState(false);
  const [planDialog, setPlanDialog] = useState(false);
  const [rateLimitDialog, setRateLimitDialog] = useState(null); // keyId
  const [confirmRevoke, setConfirmRevoke] = useState(null);      // keyId

  if (loading && !detail) {
    return (
      <Card>
        <Card.Body>
          <div className="p-12 flex items-center justify-center">
            <Spinner size={24} />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (detail?.error) {
    return (
      <Card>
        <Card.Body>
          <div className="p-8 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto text-destructive mb-2" />
            <div className="text-sm text-destructive">{detail.error}</div>
            <Button variant="ghost" onClick={onClose} className="mt-3">Close</Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!detail) return null;

  const c = detail;
  const keys = c.keys || [];
  const activeKeys = keys.filter((k) => k.active);
  const lastActive = c.last_active_at;

  return (
    <div className="space-y-4">
      {/* Header card */}
      <Card>
        <Card.Body>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar email={c.email} plan={c.plan_code} />
              <div className="min-w-0">
                <div className="text-lg font-semibold text-fg font-mono truncate">{c.email}</div>
                <CopyableId id={c.id} />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Badge variant={c.plan_code === 'free' ? 'default' : 'brand'}>
                    {c.plan_code}
                  </Badge>
                  <Badge variant="default">
                    <CreditCard className="h-3 w-3" /> {formatNumber(c.credit_balance || 0)} credits
                  </Badge>
                  <Badge variant="default">
                    <Activity className="h-3 w-3" /> {formatNumber(c.month_calls || 0)} calls / 30d
                  </Badge>
                  {lastActive && (
                    <Badge variant="default">
                      <span className="font-mono text-2xs">last: {formatRelative(lastActive)}</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
              Close
            </Button>
          </div>

          {/* Quick stats */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MiniStat label="Purchased" value={formatNumber(c.total_credits_purchased || 0)} />
            <MiniStat label="Consumed"  value={formatNumber(c.total_credits_consumed || 0)} />
            <MiniStat label="Active keys" value={activeKeys.length} />
            <MiniStat label="Bandwidth (30d)" value={formatCompact(c.month_bytes || 0)} />
          </div>

          {/* Action row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="primary" onClick={() => setCreditsDialog(true)}>
              <Plus className="h-3.5 w-3.5" />
              Add credits
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setPlanDialog(true)}>
              <CreditCard className="h-3.5 w-3.5" />
              Change plan
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => copyToClipboard(`sk_${c.id.slice(0, 8)}...`)}
            >
              <Copy className="h-3.5 w-3.5" />
              Copy reference
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Subscription — Phase 1+ schema */}
      {c.active_subscription && (
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-fg-muted" />
              <div>
                <Card.Title>Active subscription</Card.Title>
                <Card.Subtitle>
                  {c.active_subscription.billing_interval === 'quarterly'
                    ? 'Quarterly (20% off)'
                    : c.active_subscription.billing_interval === 'monthly'
                    ? 'Monthly'
                    : 'Custom interval'}
                </Card.Subtitle>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <MiniStat label="Plan" value={c.active_subscription.plan_code} />
            <MiniStat
              label="Period start"
              value={c.active_subscription.period_start
                ? new Date(c.active_subscription.period_start).toLocaleDateString()
                : '—'}
            />
            <MiniStat
              label="Period end"
              value={c.active_subscription.period_end
                ? new Date(c.active_subscription.period_end).toLocaleDateString()
                : '—'}
            />
            <MiniStat
              label="Grace until"
              value={c.active_subscription.period_grace_until
                ? new Date(c.active_subscription.period_grace_until).toLocaleDateString()
                : '—'}
            />
            <MiniStat
              label="Last grant"
              value={c.active_subscription.last_credit_grant_at
                ? new Date(c.active_subscription.last_credit_grant_at).toLocaleDateString()
                : '—'}
            />
            <MiniStat label="Status" value={c.active_subscription.status} />
          </Card.Body>
        </Card>
      )}

      {/* API keys */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-fg-muted" />
            <div>
              <Card.Title>API keys</Card.Title>
              <Card.Subtitle>{keys.length} total · {activeKeys.length} active</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {keys.length === 0 ? (
            <div className="p-8 text-center text-xs text-fg-muted">No keys yet.</div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-border bg-surface-2/30">
                <tr className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
                  <th className="text-left px-5 py-2">Prefix</th>
                  <th className="text-left px-5 py-2">Label</th>
                  <th className="text-right px-5 py-2">Rate / min</th>
                  <th className="text-right px-5 py-2">Last used</th>
                  <th className="text-right px-5 py-2">Status</th>
                  <th className="text-right px-5 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-border last:border-b-0">
                    <td className="px-5 py-3 text-sm font-mono text-fg">{k.key_prefix}</td>
                    <td className="px-5 py-3 text-sm text-fg">{k.label || <span className="text-fg-subtle italic">(no label)</span>}</td>
                    <td className="px-5 py-3 text-xs text-fg-muted text-right tabular-nums">{k.rate_limit_per_minute}</td>
                    <td className="px-5 py-3 text-xs text-fg-muted text-right">{k.last_used_at ? formatRelative(k.last_used_at) : <span className="text-fg-subtle">never</span>}</td>
                    <td className="px-5 py-3 text-right">
                      {k.active ? (
                        <Badge variant="success"><Dot variant="success" /> active</Badge>
                      ) : (
                        <Badge variant="destructive">revoked</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {k.active && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setRateLimitDialog(k)}
                            className="text-2xs text-fg-muted hover:text-fg px-2 py-1 rounded hover:bg-surface-2 transition-colors"
                          >
                            <Activity className="h-3 w-3 inline mr-0.5" /> Limit
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRevoke(k)}
                            className="text-2xs text-destructive hover:bg-destructive/10 px-2 py-1 rounded transition-colors"
                          >
                            <Trash2 className="h-3 w-3 inline mr-0.5" /> Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {/* Action dialogs */}
      <AddCreditsDialog
        open={creditsDialog}
        onClose={() => setCreditsDialog(false)}
        customerId={c.id}
        onDone={(msg) => { setCreditsDialog(false); alert(msg); onChanged(); }}
      />
      <ChangePlanDialog
        open={planDialog}
        onClose={() => setPlanDialog(false)}
        customerId={c.id}
        currentPlan={c.plan_code}
        onDone={(msg) => { setPlanDialog(false); alert(msg); onChanged(); }}
      />
      <RateLimitDialog
        keyObj={rateLimitDialog}
        onClose={() => setRateLimitDialog(null)}
        onDone={() => { setRateLimitDialog(null); onChanged(); }}
      />
      <RevokeKeyDialog
        keyObj={confirmRevoke}
        onClose={() => setConfirmRevoke(null)}
        onDone={() => { setConfirmRevoke(null); onChanged(); }}
      />
    </div>
  );
}

function CopyableId({ id }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="mt-0.5 inline-flex items-center gap-1 text-2xs text-fg-muted hover:text-fg font-mono"
    >
      <span className="opacity-70">{id.slice(0, 8)}…</span>
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3 opacity-50" />}
    </button>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-surface-2/50 border border-border p-2.5">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-sm text-fg font-medium mt-0.5 truncate">{value}</div>
    </div>
  );
}

/* =============================================================================
   ACTION DIALOGS
   ============================================================================= */
function AddCreditsDialog({ open, onClose, customerId, onDone }) {
  const [credits, setCredits] = useState(1000);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setCredits(1000); setNote(''); setErr(null); }
  }, [open]);

  async function submit(e) {
    e?.preventDefault?.();
    setBusy(true); setErr(null);
    try {
      await adminFetch(`/admin/customers/${customerId}/credits`, {
        method: 'POST',
        // Backend expects flat 'credits' + 'description' (Pydantic Body fields)
        body: { credits: Number(credits), description: note.trim() || `admin granted ${credits} credits` },
      });
      onDone?.(`Granted ${formatNumber(credits)} credits.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Add credits" description="Bumps the customer's balance and logs a ledger entry." size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !credits}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add credits
          </Button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <Field label="Credits to add" hint="Positive integer">
          <Input
            type="number"
            min={1}
            value={credits}
            onChange={(e) => setCredits(parseInt(e.target.value) || 0)}
            autoFocus
            className="font-mono"
          />
        </Field>
        <Field label="Note" hint="Visible in the customer's credit ledger">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. support_credit"
            className="font-mono"
          />
        </Field>
        {err && (
          <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {err}
          </div>
        )}
      </form>
    </Dialog>
  );
}

function ChangePlanDialog({ open, onClose, customerId, currentPlan, onDone }) {
  const [plan, setPlan] = useState(currentPlan || 'free');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (open) { setPlan(currentPlan || 'free'); setErr(null); } }, [open, currentPlan]);

  async function submit(e) {
    e?.preventDefault?.();
    setBusy(true); setErr(null);
    try {
      await adminFetch(`/admin/customers/${customerId}/plan`, {
        method: 'PATCH',
        body: { plan_code: plan },
      });
      onDone?.(`Plan changed to ${plan}.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Change plan" description="Manual override. Use sparingly — prefer the customer's own billing flow." size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Change plan'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="New plan">
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
          >
            {['free', 'starter', 'pro', 'enterprise', 'custom'].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
        {plan !== currentPlan && (
          <div className="px-3 py-2 rounded-md bg-warning/10 border border-warning/30 text-xs text-warning flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>This will override the customer's current plan (<strong>{currentPlan}</strong>). Their active subscription is unaffected.</span>
          </div>
        )}
        {err && (
          <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {err}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function RateLimitDialog({ keyObj, onClose, onDone }) {
  const [limit, setLimit] = useState(60);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (keyObj) { setLimit(keyObj.rate_limit_per_minute || 60); setErr(null); }
  }, [keyObj]);

  if (!keyObj) return null;

  async function submit(e) {
    e?.preventDefault?.();
    setBusy(true); setErr(null);
    try {
      await adminFetch(`/admin/keys/${keyObj.id}/limits`, {
        method: 'PATCH',
        body: { rate_limit_per_minute: Number(limit) },
      });
      onDone?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={!!keyObj} onClose={onClose} title="Per-key rate limit" description={`Override for ${keyObj.key_prefix}`} size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <Field label="Requests per minute" hint="0 = use plan default">
          <Input
            type="number"
            min={0}
            max={10000}
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
            autoFocus
            className="font-mono"
          />
        </Field>
        {err && (
          <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {err}
          </div>
        )}
      </form>
    </Dialog>
  );
}

function RevokeKeyDialog({ keyObj, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!keyObj) return null;

  async function submit() {
    setBusy(true); setErr(null);
    try {
      await adminFetch(`/admin/keys/${keyObj.id}`, { method: 'DELETE' });
      onDone?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={!!keyObj} onClose={onClose} title="Revoke API key?" size="sm"
      description={`This will immediately block any request using ${keyObj.key_prefix}. Irreversible.`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Revoke key
          </Button>
        </div>
      }
    >
      <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Existing requests using this key will start returning 401. The customer can mint a replacement key from their dashboard.</span>
      </div>
      {err && (
        <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-center gap-2 mt-3">
          <AlertTriangle className="h-3.5 w-3.5" /> {err}
        </div>
      )}
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-2xs font-medium text-fg-muted">{label}</label>
          {hint && <span className="text-2xs text-fg-subtle">{hint}</span>}
        </div>
      )}
      {children}
    </div>
  );
}
